import { useEffect, useMemo, useState } from "react";
import { Box, Button, Paper, Stack, Typography } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom-v5-compat";
import {
  clearDemoTourProgress,
  DEMO_TOUR_ENABLED_KEY,
  DEMO_TOUR_FEATURE_ENABLED,
  DEMO_TOUR_STATE_KEY,
  stopDemoTour,
} from "./demoTourStorage";

const readTourState = () => {
  try {
    return JSON.parse(window.localStorage.getItem(DEMO_TOUR_STATE_KEY)) || {};
  } catch (err) {
    return {};
  }
};

const writeTourState = (state) => {
  window.localStorage.setItem(DEMO_TOUR_STATE_KEY, JSON.stringify(state));
};

const tourColors = {
  accent: "#3a7bd5",
  accentHover: "#5d95de",
  navy: "#253c53",
};

const steps = [
  {
    path: "/dashboard/fleet",
    title: "Start with the fleet dashboard",
    body: "This demo opens with the executive view: live fleet KPIs, safety score, utilisation, and alerts in one place.",
  },
  {
    path: "/dashboard/fleet",
    selector: '[data-demo-tour="dashboard-overview"]',
    title: "Fleet health at a glance",
    body: "The overview cards turn raw telematics into operating signals your team can scan in seconds.",
  },
  {
    path: "/dashboard/fleet",
    selector: '[data-demo-tour="dashboard-tools"]',
    title: "Fast paths into daily workflows",
    body: "These shortcuts move users from high-level fleet status into tracking, safety, maintenance, and site workflows without hunting through menus.",
  },
  {
    path: "/dashboard/assets",
    selector: '[data-demo-tour="asset-map-surface"]',
    title: "A map built for large fleets",
    body: "The fleet map is designed for dense operations: clustered markers, overlays, geofences, and live asset state stay usable even when hundreds of vehicles are visible at once.",
  },
  {
    path: "/dashboard/assets",
    selector: '[data-demo-tour="contact-cta"]',
    title: "Turn the demo into a walkthrough",
    body: "This CTA is the handoff point when someone wants to see the same workflow against their own fleet.",
  },
];

const DemoAccountTour = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [active, setActive] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [targetRect, setTargetRect] = useState(null);
  const [targetElement, setTargetElement] = useState(null);
  const [targetReady, setTargetReady] = useState(false);

  useEffect(() => {
    if (!DEMO_TOUR_FEATURE_ENABLED) {
      clearDemoTourProgress();
      setActive(false);
      return;
    }

    const enabled =
      window.localStorage.getItem(DEMO_TOUR_ENABLED_KEY) === "true";

    if (!enabled) {
      setActive(false);
      return;
    }

    const state = readTourState();
    const nextStep = Number(state.stepIndex) || 0;
    setStepIndex(Math.min(Math.max(nextStep, 0), steps.length - 1));
    setActive(true);
  }, []);

  const step = steps[stepIndex];

  useEffect(() => {
    if (!active || !step) return;

    if (location.pathname !== step.path) {
      navigate(step.path, { replace: true });
    }
  }, [active, location.pathname, navigate, step]);

  useEffect(() => {
    if (!active || !step) return undefined;

    let frameId;
    let timeoutId;

    const updateTarget = () => {
      const nextTarget = step.selector
        ? document.querySelector(step.selector)
        : null;

      if (nextTarget) {
        nextTarget.scrollIntoView({ behavior: "smooth", block: "center" });
        setTargetElement(nextTarget);
        setTargetRect(nextTarget.getBoundingClientRect());
        setTargetReady(true);
      } else {
        setTargetElement(null);
        setTargetRect(null);
        setTargetReady(!step.selector);
        window.clearTimeout(timeoutId);
        timeoutId = window.setTimeout(updateTarget, 150);
      }
    };

    setTargetReady(!step.selector);
    setTargetElement(null);
    setTargetRect(null);
    frameId = window.requestAnimationFrame(updateTarget);
    window.addEventListener("resize", updateTarget);
    window.addEventListener("scroll", updateTarget, true);

    return () => {
      window.cancelAnimationFrame(frameId);
      window.clearTimeout(timeoutId);
      window.removeEventListener("resize", updateTarget);
      window.removeEventListener("scroll", updateTarget, true);
    };
  }, [active, location.pathname, step]);

  useEffect(() => {
    if (!active) return;

    writeTourState({
      stepIndex,
      updatedAt: Date.now(),
    });
  }, [active, stepIndex]);

  const cardPosition = useMemo(() => {
    if (!targetRect) {
      return {
        left: "50%",
        top: "50%",
        transform: "translate(-50%, -50%)",
      };
    }

    const width = Math.min(380, window.innerWidth - 32);
    const rightSpace = window.innerWidth - targetRect.right;
    const left =
      rightSpace >= width + 24
        ? targetRect.right + 16
        : Math.max(
            16,
            Math.min(targetRect.left, window.innerWidth - width - 16)
          );
    const top = Math.max(
      16,
      Math.min(targetRect.bottom + 14, window.innerHeight - 280)
    );

    return {
      left,
      top,
      width,
    };
  }, [targetRect]);

  if (!DEMO_TOUR_FEATURE_ENABLED || !active || !step || !targetReady) {
    return null;
  }

  const isLastStep = stepIndex === steps.length - 1;
  const body =
    typeof step.body === "function" ? step.body(targetElement) : step.body;

  const completeTour = () => {
    stopDemoTour();
    writeTourState({
      stepIndex: steps.length - 1,
      updatedAt: Date.now(),
    });
    setActive(false);
  };

  const goBack = () => {
    setTargetReady(false);
    setStepIndex((current) => Math.max(0, current - 1));
  };

  const goNext = () => {
    if (isLastStep) {
      completeTour();
      return;
    }

    setTargetReady(false);
    setStepIndex((current) => Math.min(steps.length - 1, current + 1));
  };

  return (
    <>
      {!targetRect && (
        <Box
          sx={{
            bgcolor: "rgba(4, 10, 26, 0.54)",
            bottom: 0,
            left: 0,
            pointerEvents: "none",
            position: "fixed",
            right: 0,
            top: 0,
            zIndex: 1500,
          }}
        />
      )}

      {targetRect && (
        <Box
          sx={{
            borderRadius: 1,
            boxShadow: "0 0 0 9999px rgba(4, 10, 26, 0.36)",
            height: targetRect.height + 12,
            left: targetRect.left - 6,
            pointerEvents: "none",
            position: "fixed",
            top: targetRect.top - 6,
            width: targetRect.width + 12,
            zIndex: 1501,
          }}
        />
      )}

      <Paper
        elevation={8}
        sx={{
          animation: "demoCardIn 220ms ease-out",
          background:
            "linear-gradient(145deg, rgba(255,255,255,0.98) 0%, rgba(244,250,255,0.98) 100%)",
          border: "1px solid rgba(58, 123, 213, 0.22)",
          borderRadius: 1,
          boxShadow:
            "0 22px 60px rgba(15, 23, 42, 0.28), 0 0 0 1px rgba(255,255,255,0.76) inset",
          overflow: "hidden",
          p: 2.25,
          position: "fixed",
          zIndex: 1502,
          "@keyframes demoCardIn": {
            from: {
              opacity: 0,
              transform: targetRect
                ? "translateY(8px)"
                : "translate(-50%, calc(-50% + 8px))",
            },
            to: {
              opacity: 1,
              transform: targetRect ? "translateY(0)" : "translate(-50%, -50%)",
            },
          },
          ...cardPosition,
        }}
      >
        <Stack spacing={1.75}>
          <Stack alignItems="center" direction="row" spacing={1.25}>
            <Box
              sx={{
                bgcolor: tourColors.navy,
                borderRadius: 1,
                color: "#ffffff",
                flexShrink: 0,
                fontSize: 12,
                fontWeight: 800,
                letterSpacing: 0.4,
                lineHeight: "24px",
                minWidth: 42,
                textAlign: "center",
              }}
            >
              {stepIndex + 1}/{steps.length}
            </Box>
            <Typography sx={{ fontWeight: 700 }} variant="subtitle1">
              {step.title}
            </Typography>
          </Stack>

          <Typography color="text.secondary" variant="body2">
            {body}
          </Typography>

          <Stack direction="row" spacing={0.75}>
            {steps.map((_, index) => (
              <Box
                key={index}
                sx={{
                  bgcolor:
                    index <= stepIndex
                      ? tourColors.accent
                      : "rgba(37, 60, 83, 0.16)",
                  borderRadius: 1,
                  flex: 1,
                  height: 6,
                  transition:
                    "background-color 180ms ease, transform 180ms ease",
                  transform: index === stepIndex ? "scaleY(1.35)" : "scaleY(1)",
                }}
              />
            ))}
          </Stack>

          <Stack direction="row" justifyContent="space-between" spacing={1}>
            <Button color="inherit" onClick={completeTour} size="small">
              Skip
            </Button>
            <Stack direction="row" spacing={1}>
              <Button
                disabled={stepIndex === 0}
                onClick={goBack}
                size="small"
                variant="text"
              >
                Back
              </Button>
              <Button
                onClick={goNext}
                size="small"
                sx={{
                  bgcolor: isLastStep ? tourColors.accent : undefined,
                  color: isLastStep ? "#ffffff" : undefined,
                  fontWeight: 700,
                  overflow: "hidden",
                  position: "relative",
                  textTransform: "none",
                  "&:hover": {
                    bgcolor: isLastStep ? tourColors.accentHover : undefined,
                  },
                  ...(isLastStep && {
                    "&::after": {
                      animation: "demoButtonSheen 2.4s ease-in-out infinite",
                      background:
                        "linear-gradient(105deg, transparent 20%, rgba(255,255,255,0.72) 48%, transparent 76%)",
                      content: '""',
                      height: "170%",
                      left: "-60%",
                      position: "absolute",
                      top: "-35%",
                      width: "42%",
                    },
                    "@keyframes demoButtonSheen": {
                      "0%": {
                        transform: "translateX(0)",
                      },
                      "62%, 100%": {
                        transform: "translateX(390%)",
                      },
                    },
                  }),
                }}
                variant="contained"
              >
                {isLastStep ? "Close" : "Next"}
              </Button>
            </Stack>
          </Stack>
        </Stack>
      </Paper>
    </>
  );
};

export default DemoAccountTour;
